import { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatDate, STATUSES, STATUS_STYLES, cn } from "@/lib/utils";
import {
  ArrowLeft, Phone, Envelope, User, Calendar, Tag, ChatCircleText, FloppyDisk,
  Trash, CaretRight, CaretDown, Sparkle, PencilSimple, X, Plus, ArrowUp, ArrowDown,
  Users as UsersIcon, MagnifyingGlass, Buildings, Trophy, ChartLineDown, Hash, Info,
  FilePdf, ArrowSquareOut,
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import SlaTimer from "@/components/SlaTimer";

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  const isGuest = (user?.role || "").toLowerCase() === "guest";
  const [lead, setLead] = useState(null);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [lostReasons, setLostReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [threadSort, setThreadSort] = useState("newest"); // "oldest" | "newest"
  const [statusDialog, setStatusDialog] = useState(null); // null | "won" | "lost"
  // Per-message body view mode. Default: HTML if available, else Plain. The
  // toggle below each thread message lets users flip between the two.
  const [bodyMode, setBodyMode] = useState({}); // { [idx]: "html" | "plain" }
  // Manual lead-split mode. When `splitMode` is true, each thread message
  // gets a checkbox; selected indices spin off into a brand-new lead via
  // POST /api/leads/{id}/split. Admins use this to unmerge historic
  // conflations that pre-date the v7.9 dedup tightening.
  const [splitMode, setSplitMode] = useState(false);
  const [splitSelection, setSplitSelection] = useState(new Set());
  const [splitting, setSplitting] = useState(false);
  // Identity groups returned by /api/leads/{id}/split/analyze. Surfaced as
  // optional "Suggested groups" chips below — clicking a chip selects all of
  // that group's messages at once. Individual checkboxes remain fully manual.
  const [splitGroups, setSplitGroups] = useState([]);

  const [resplitting, setResplitting] = useState(false);
  const [rescuingIdx, setRescuingIdx] = useState(null);

  const rescueMessage = async (idx, msg) => {
    if (!lead?.id) return;
    if (!window.confirm(
      `Split "${msg.subject || "this message"}" from ${msg.from_name || msg.from_email || "unknown"} into its own lead? `
      + `It will be treated like a brand-new lead (AI extraction, auto-routing and new-lead notification).`
    )) return;
    setRescuingIdx(idx);
    try {
      const { data: newLead } = await api.post(`/leads/${lead.id}/split`, { message_indices: [idx], notify: true });
      toast.success(`New lead created: ${newLead.name || newLead.email_subject || "Untitled"}`, {
        action: { label: "Open", onClick: () => navigate(`/leads/${newLead.id}`) },
        duration: 10000,
      });
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Split failed");
    } finally {
      setRescuingIdx(null);
    }
  };

  // Detect distinct case references (C1234567, #12345, LITIGATION SHEET numbers)
  // across the thread — surface a one-click "Resplit by case ref" button when
  // the thread carries messages for 2+ separate matters.
  const distinctCaseRefs = useMemo(() => {
    const re = /(^|[^A-Z0-9])([A-Z]\d{5,10}|\d{5,}\/\d{2,}|#\d{4,})/gi;
    const set = new Set();
    for (const t of lead?.thread || []) {
      const src = `${t?.subject || ""} ${t?.body?.slice?.(0, 200) || ""}`;
      let m;
      while ((m = re.exec(src)) !== null) set.add(m[2].toUpperCase());
    }
    return Array.from(set);
  }, [lead]);

  const resplitByCaseRef = async () => {
    if (!lead?.id) return;
    if (!window.confirm(
      `Split this lead into ${distinctCaseRefs.length} separate leads (one per case ref)? `
      + `The primary case ref stays here with its current assignee / status / notes / matter number intact. `
      + `Each extra ref becomes a fresh 'new' lead.`
    )) return;
    setResplitting(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/resplit-by-case-ref`);
      const spinoffs = data.created_count || 0;
      toast.success(
        `Split into ${spinoffs + 1} leads. Primary: ${data.primary_case_ref}. `
        + `Created ${spinoffs} new lead${spinoffs === 1 ? "" : "s"}.`
      );
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resplit failed");
    } finally {
      setResplitting(false);
    }
  };

  const enterSplitMode = async () => {
    setSplitMode(true);
    setSplitSelection(new Set());
    try {
      const { data } = await api.get(`/leads/${id}/split/analyze`);
      // Only keep groups that have at least one message OTHER than the
      // original (idx 0) — the original always stays put.
      const groups = (data?.groups || []).filter((g) =>
        (g.message_indices || []).some((i) => i > 0)
      );
      setSplitGroups(groups);
    } catch (e) {
      // Non-fatal — manual selection still works.
      console.debug("split/analyze failed", e);
      setSplitGroups([]);
    }
  };

  const exitSplitMode = () => {
    setSplitMode(false);
    setSplitSelection(new Set());
    setSplitGroups([]);
  };

  // Per-checkbox toggle — strictly one message at a time. No auto-include of
  // "linked" replies, because the identity heuristic can't reliably tell which
  // replies belong to which prospect when senders/email signals overlap. Users
  // tick exactly the messages they want; the Suggested-Groups chips below
  // remain a convenience for selecting a whole detected group in one click.
  const toggleSplitIdx = (idx) => {
    if (idx === 0) return; // Original always stays
    setSplitSelection((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const fetchAll = async () => {
    try {
      const [a, b, c] = await Promise.all([
        api.get(`/leads/${id}`),
        api.get(`/attorneys`),
        api.get(`/settings/lookups`).catch(() => ({ data: { lost_reasons: [] } })),
      ]);
      setLead(a.data);
      setAssignableUsers(b.data);
      setLostReasons(Array.isArray(c.data?.lost_reasons) ? c.data.lost_reasons : []);
    } catch {
      toast.error("Failed to load lead");
      navigate("/leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [id]);

  const save = async (patch, { silent = false } = {}) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/leads/${id}`, patch);
      setLead(data);
      if (!silent) toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Lead deleted");
      navigate("/leads");
    } catch {
      toast.error("Delete failed");
    }
  };

  // ---- Assignment helpers ----
  const assignedEmails = useMemo(
    () => (Array.isArray(lead?.assigned_to) ? lead.assigned_to.map((e) => (e || "").toLowerCase()) : []),
    [lead],
  );
  const assignedDetails = useMemo(
    () => assignedEmails.map((e) => assignableUsers.find((u) => (u.email || u.user_principal_name || "").toLowerCase() === e) || { email: e, display_name: e }),
    [assignedEmails, assignableUsers],
  );

  const setAssignees = (emails) => {
    const lowered = emails.map((e) => (e || "").toLowerCase());
    const names = lowered.map((e) => {
      const u = assignableUsers.find((x) => (x.email || x.user_principal_name || "").toLowerCase() === e);
      return u?.display_name || e;
    });
    save({ assigned_to: lowered, assigned_to_name: names });
  };

  const removeAssignee = (email) => {
    setAssignees(assignedEmails.filter((e) => e !== email));
  };

  // ---- Departments involved (derived from assignees) ----
  const departments = useMemo(() => {
    const set = new Set();
    assignedDetails.forEach((a) => {
      if (a?.department) set.add(a.department);
    });
    return Array.from(set);
  }, [assignedDetails]);

  // ---- Editable title ----
  const [titleEditing, setTitleEditing] = useState(false);
  const [titleDraft, setTitleDraft] = useState(lead?.name || "");
  useEffect(() => { setTitleDraft(lead?.name || ""); }, [lead?.name]);
  const commitTitle = () => {
    setTitleEditing(false);
    const v = titleDraft.trim();
    if (v === (lead?.name || "").trim()) return;
    save({ name: v || null });
  };

  // ---- Thread sort ----
  const sortedThread = useMemo(() => {
    const t = [...(lead?.thread || [])];
    // Index of original message (idx==0 in stored order) is always preserved for "Original" tag
    return t.map((m, idx) => ({ ...m, _origIdx: idx })).sort((a, b) => {
      const at = a.sent_at ? new Date(a.sent_at).getTime() : 0;
      const bt = b.sent_at ? new Date(b.sent_at).getTime() : 0;
      return threadSort === "oldest" ? at - bt : bt - at;
    });
  }, [lead, threadSort]);

  if (loading || !lead) {
    return <div className="text-sm text-slate-500" data-testid="lead-loading">Loading…</div>;
  }

  return (
    <div data-testid="lead-detail-page" className="space-y-8 w-full">
      {/* Back + actions */}
      <div className="flex items-center justify-between">
        <Link to="/leads" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900">
          <ArrowLeft size={16} /> Back to leads
        </Link>
        {isGuest ? (
          <span data-testid="lead-guest-readonly" className="px-3 py-1 text-xs rounded-sm bg-slate-100 text-slate-600 border border-slate-200">
            Read-only view
          </span>
        ) : (
          <Button
            data-testid="delete-lead-btn"
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 rounded-sm gap-2"
          >
            <Trash size={14} /> Delete
          </Button>
        )}
      </div>

      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <div className="flex items-center gap-3 mb-2">
          <Sparkle size={14} className="text-[#002FA7]" weight="fill" />
          <div className="label-tracking">AI-Extracted Lead</div>
        </div>
        <h1
          data-testid="lead-title"
          className="font-display text-4xl sm:text-5xl tracking-tight leading-none text-slate-900 group relative inline-block max-w-full"
        >
          {titleEditing ? (
            <input
              autoFocus
              data-testid="lead-title-input"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={commitTitle}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitTitle();
                if (e.key === "Escape") { setTitleDraft(lead.name || ""); setTitleEditing(false); }
              }}
              placeholder="Lead title…"
              className="font-display text-4xl sm:text-5xl tracking-tight leading-none text-slate-900 bg-transparent border-b-2 border-[#002FA7] outline-none w-full max-w-[640px] py-1"
            />
          ) : (
            <button
              type="button"
              data-testid="lead-title-edit"
              onClick={() => { setTitleDraft(lead.name || ""); setTitleEditing(true); }}
              className="text-left hover:text-[#002FA7] transition-colors inline-flex items-center gap-2"
              title="Click to edit"
            >
              <span>{lead.name || <span className="italic text-slate-400">Unknown contact</span>}</span>
              <PencilSimple size={18} className="opacity-0 group-hover:opacity-50 text-slate-500" />
            </button>
          )}
        </h1>
        <div className="flex flex-wrap items-center gap-3 mt-4">
          {assignedDetails.length > 0 && (
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-700">
              <UsersIcon size={12} className="text-slate-400" />
              {assignedDetails.length === 1
                ? assignedDetails[0].display_name
                : `${assignedDetails.length} assigned`}
            </span>
          )}
          {departments.length > 0 && (
            <span
              data-testid="lead-departments"
              className="inline-flex items-center gap-1.5 text-xs text-indigo-700 bg-indigo-50 px-2 py-1 rounded-sm"
              title={departments.join(", ")}
            >
              <Buildings size={12} weight="fill" />
              {departments.length === 1
                ? departments[0]
                : `${departments.length} departments · ${departments.join(" + ")}`}
            </span>
          )}
          <SlaTimer lead={lead} size="lg" />
          <StatusBadge status={lead.status} />
          {lead.source && (
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-700 font-mono">
              <Tag size={12} /> {lead.source}
            </span>
          )}
          {lead.urgency && (
            <span className="inline-flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded-sm">
              Urgency: {lead.urgency}
            </span>
          )}
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: details + thread */}
        <div className="lg:col-span-2 space-y-6">
          {/* Extracted details card */}
          <section className="bg-white border border-slate-200 rounded-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="label-tracking">Extracted Details</div>
              <span className="text-[0.65rem] text-slate-400">Click a field to edit</span>
            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6">
              <EditableField
                label="Name"
                value={lead.name}
                placeholder="Add name"
                icon={User}
                testid="edit-name"
                readOnly={isGuest}
                onSave={(v) => save({ name: v || null })}
              />
              <EditableField
                label="Telephone"
                value={lead.phone}
                placeholder="Add phone"
                icon={Phone}
                mono
                testid="edit-phone"
                readOnly={isGuest}
                onSave={(v) => save({ phone: v || null })}
              />
              <div className="sm:col-span-2">
                <EditableField
                  label="Email"
                  value={lead.email}
                  placeholder="Add email address"
                  icon={Envelope}
                  type="email"
                  testid="edit-email"
                  link={lead.email ? `mailto:${lead.email}` : null}
                  readOnly={isGuest}
                  onSave={(v) => save({ email: v || null })}
                />
              </div>
              <div className="sm:col-span-2">
                <dt className="text-xs text-slate-500 mb-1">AI Summary</dt>
                <dd className="text-sm text-slate-800 leading-relaxed">
                  {lead.summary || <span className="text-slate-400">No summary available.</span>}
                </dd>
              </div>
              <div className="sm:col-span-2 pt-2 border-t border-slate-100">
                <dt className="text-xs text-slate-500 mb-1">Email Metadata</dt>
                <dd className="text-xs text-slate-600 space-y-1">
                  <div><span className="text-slate-400">Subject:</span> {lead.email_subject || "—"}</div>
                  <div><span className="text-slate-400">From:</span> {lead.email_from_name || lead.email_from || "—"} {lead.email_from && <span className="font-mono">&lt;{lead.email_from}&gt;</span>}</div>
                  <div className="inline-flex items-center gap-1.5"><Calendar size={12} className="text-slate-400" />{formatDate(lead.received_at)}</div>
                </dd>
              </div>
            </dl>
          </section>

          {/* Thread */}
          <section className="bg-white border border-slate-200 rounded-sm p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="label-tracking inline-flex items-center gap-2">
                <ChatCircleText size={14} /> Conversation Thread ({lead.thread?.length || 0})
              </div>
              <div className="inline-flex items-center gap-2">
                {isAdmin && distinctCaseRefs.length >= 2 && !splitMode && (
                  <button
                    data-testid="thread-resplit-caseref-btn"
                    type="button"
                    disabled={resplitting}
                    onClick={resplitByCaseRef}
                    className="text-[0.7rem] inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border border-sky-300 bg-sky-50 hover:bg-sky-100 text-sky-900 disabled:opacity-50"
                    title={`Detected ${distinctCaseRefs.length} case refs: ${distinctCaseRefs.join(", ")}. Auto-split into one lead per ref.`}
                  >
                    {resplitting ? "Splitting…" : `Split by case ref (${distinctCaseRefs.length})`}
                  </button>
                )}
                {isAdmin && (lead.thread?.length || 0) > 1 && !splitMode && (
                  <button
                    data-testid="thread-split-toggle"
                    type="button"
                    onClick={enterSplitMode}
                    className="text-[0.7rem] inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900"
                    title="Move selected messages into a new lead"
                  >
                    Split lead
                  </button>
                )}
                <button
                  data-testid="thread-sort-toggle"
                  type="button"
                  onClick={() => setThreadSort((p) => (p === "oldest" ? "newest" : "oldest"))}
                  className="text-[0.7rem] inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border border-slate-200 hover:bg-slate-50 text-slate-600"
                  title="Toggle sort order"
                >
                  {threadSort === "oldest"
                    ? <><ArrowDown size={11} /> Oldest first</>
                    : <><ArrowUp size={11} /> Newest first</>}
                </button>
              </div>
            </div>

            {splitMode && (
              <div data-testid="split-mode-bar" className="mb-4 rounded-sm border border-amber-200 bg-amber-50 p-3 space-y-2 text-xs">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="text-amber-900">
                    <strong>Split mode</strong> — tick the exact messages that should move to a new lead. Each checkbox is independent. The Original message cannot be moved.
                  </div>
                  <div className="inline-flex items-center gap-2">
                    <span data-testid="split-selected-count" className="font-mono text-amber-900">{splitSelection.size} selected</span>
                    <button
                      type="button"
                      onClick={exitSplitMode}
                      className="px-2 py-1 rounded-sm border border-amber-300 hover:bg-amber-100 text-amber-900"
                    >Cancel</button>
                    <button
                      data-testid="split-apply-btn"
                      type="button"
                      disabled={splitting || splitSelection.size === 0 || splitSelection.size >= (lead.thread?.length || 0)}
                      onClick={async () => {
                        if (splitSelection.size === 0) return;
                        setSplitting(true);
                        try {
                          const indices = Array.from(splitSelection).sort((a, b) => a - b);
                          const { data: newLead } = await api.post(`/leads/${id}/split`, { message_indices: indices });
                          toast.success(`Split off ${indices.length} message(s) → ${newLead.name || "new lead"}`);
                          exitSplitMode();
                          // refresh current lead + navigate to new one in a new tab
                          await fetchAll();
                          window.open(`/leads/${newLead.id}`, "_blank");
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Split failed");
                        } finally {
                          setSplitting(false);
                        }
                      }}
                      className="px-3 py-1 rounded-sm bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {splitting ? "Splitting…" : `Create new lead with ${splitSelection.size}`}
                    </button>
                  </div>
                </div>
                {splitGroups.length > 0 && (
                  <div data-testid="split-group-chips" className="pt-2 border-t border-amber-200 flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider text-amber-700">Suggested groups:</span>
                    {splitGroups.map((g, gi) => {
                      const selectable = (g.message_indices || []).filter((i) => i > 0);
                      if (selectable.length === 0) return null;
                      const allSelected = selectable.every((i) => splitSelection.has(i));
                      const label = g.from_name || g.from_email || (g.phones?.[0]) || (g.emails?.[0]) || `Group ${gi + 1}`;
                      return (
                        <button
                          type="button"
                          key={gi}
                          data-testid={`split-group-chip-${gi}`}
                          onClick={() => {
                            setSplitSelection((prev) => {
                              const next = new Set(prev);
                              if (allSelected) selectable.forEach((i) => next.delete(i));
                              else selectable.forEach((i) => next.add(i));
                              return next;
                            });
                          }}
                          className={cn(
                            "text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-sm border transition-colors",
                            allSelected
                              ? "border-amber-500 bg-amber-500 text-white"
                              : "border-amber-300 bg-white hover:bg-amber-100 text-amber-900"
                          )}
                          title={`Auto-select all ${selectable.length} message(s) for this prospect`}
                        >
                          {label} <span className="opacity-70">({selectable.length})</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            <div className="space-y-3">
              {sortedThread.map((msg) => {
                const idx = msg._origIdx;
                const isOpen = !!expanded[idx];
                const isOriginal = idx === 0;
                const isSelectedForSplit = splitSelection.has(idx);
                return (
                  <div
                    key={msg.id || idx}
                    className={cn(
                      "border rounded-sm transition-colors",
                      isSelectedForSplit
                        ? "border-amber-400 bg-amber-50/60 ring-2 ring-amber-200"
                        : isOriginal
                          ? "border-[#002FA7]/30 bg-[#002FA7]/[0.02]"
                          : "border-slate-200 bg-white"
                    )}
                    data-testid={`thread-msg-${idx}`}
                  >
                    <div className="w-full p-4 flex items-start gap-3">
                      {splitMode && (
                        <div
                          className="mt-1 shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            data-testid={`split-checkbox-${idx}`}
                            checked={isSelectedForSplit}
                            disabled={isOriginal}
                            onCheckedChange={() => toggleSplitIdx(idx)}
                            title={isOriginal ? "The original message must stay on this lead" : "Tick to move this message to the new lead"}
                          />
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => setExpanded((p) => ({ ...p, [idx]: !isOpen }))}
                        className="flex-1 text-left flex items-start gap-3 min-w-0"
                      >
                        <span className="mt-1 text-slate-400">
                          {isOpen ? <CaretDown size={14} /> : <CaretRight size={14} />}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm text-slate-900">{msg.from_name || msg.from_email || "Unknown"}</span>
                            {isOriginal && <span className="text-[10px] uppercase tracking-wider bg-[#002FA7] text-white px-1.5 py-0.5 rounded-sm">Original</span>}
                            {!isOriginal && <span className="text-[10px] uppercase tracking-wider bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded-sm">Reply</span>}
                            {splitMode && isSelectedForSplit && (
                              <span className="text-[10px] uppercase tracking-wider bg-amber-500 text-white px-1.5 py-0.5 rounded-sm">Will split off</span>
                            )}
                            <span className="text-xs text-slate-400 ml-auto">{formatDate(msg.sent_at)}</span>
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">{msg.subject || lead.email_subject}</div>
                          {!isOpen && (
                            <div className="text-xs text-slate-500 mt-2 line-clamp-1">{msg.body?.slice(0, 150)}</div>
                          )}
                        </div>
                      </button>
                      {isAdmin && !isOriginal && !splitMode && (
                        <button
                          type="button"
                          data-testid={`thread-rescue-btn-${idx}`}
                          disabled={rescuingIdx !== null}
                          onClick={() => rescueMessage(idx, msg)}
                          title="Different matter? Spin this message off as its own new lead"
                          className="shrink-0 mt-0.5 text-[10px] uppercase tracking-wider inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-slate-200 text-slate-500 hover:border-amber-400 hover:bg-amber-50 hover:text-amber-900 transition-colors disabled:opacity-40"
                        >
                          <ArrowSquareOut size={11} />
                          {rescuingIdx === idx ? "Splitting…" : "Split into new lead"}
                        </button>
                      )}
                    </div>
                    <AnimatePresence>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="px-4 pb-4 pt-1 pl-10">
                            <ThreadMessageBody
                              msg={msg}
                              mode={bodyMode[idx] || (msg.body_html ? "html" : "plain")}
                              onSetMode={(m) => setBodyMode((p) => ({ ...p, [idx]: m }))}
                            />
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </section>
        </div>

        {/* Right: actions */}
        <aside className="space-y-6">
          <section className="bg-white border border-slate-200 rounded-sm p-6 space-y-4">
            <div className="label-tracking">Workflow</div>

            <div>
              <Label className="label-tracking">Status</Label>
              {isGuest ? (
                <div className="mt-2 px-3 py-2 rounded-sm border border-slate-200 bg-slate-50 text-sm capitalize text-slate-700" data-testid="status-readonly">
                  {STATUS_STYLES[lead.status]?.label || lead.status}
                </div>
              ) : (
                <Select
                  value={lead.status}
                  onValueChange={(v) => {
                    if (v === lead.status) return;
                    if (v === "won" || v === "lost") {
                      setStatusDialog(v);   // open dialog, defer save
                    } else {
                      save({ status: v });
                    }
                  }}
                >
                  <SelectTrigger data-testid="status-select" className="mt-2 rounded-sm h-10 border-slate-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s} className="capitalize">{STATUS_STYLES[s].label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* Won → Matter number */}
              {lead.status === "won" && lead.matter_number && (
                <div
                  data-testid="lead-matter-number"
                  className="mt-3 px-3 py-2 rounded-sm bg-emerald-50 border border-emerald-200 flex items-center justify-between gap-2"
                >
                  <div className="min-w-0">
                    <div className="label-tracking !text-emerald-700">Matter number</div>
                    <div className="text-sm font-mono text-emerald-900 mt-0.5 truncate">{lead.matter_number}</div>
                  </div>
                  <button
                    type="button"
                    data-testid="edit-matter-number-btn"
                    onClick={() => setStatusDialog("won")}
                    className="text-emerald-700 hover:text-emerald-900 shrink-0"
                    title="Edit matter number"
                  >
                    <PencilSimple size={14} />
                  </button>
                </div>
              )}

              {/* Won → Handover PDF download */}
              {lead.status === "won" && (
                <a
                  data-testid="download-handover-btn"
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/leads/${lead.id}/handover.pdf`}
                  onClick={async (e) => {
                    e.preventDefault();
                    try {
                      const resp = await api.get(`/leads/${lead.id}/handover.pdf`, { responseType: "blob" });
                      const cd = resp.headers["content-disposition"] || "";
                      const m = cd.match(/filename="?([^"]+)"?/);
                      const fname = m ? m[1] : `lead-${lead.id}.pdf`;
                      const url = window.URL.createObjectURL(resp.data);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = fname;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success("Handover PDF downloaded");
                    } catch {
                      toast.error("Failed to download PDF");
                    }
                  }}
                  className="mt-3 inline-flex items-center justify-center gap-2 text-xs px-3 py-2 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white transition-colors w-full font-medium"
                >
                  <FilePdf size={14} weight="fill" /> Download handover PDF
                </a>
              )}

              {/* Lost → Reason */}
              {lead.status === "lost" && lead.lost_reason && (
                <div
                  data-testid="lead-lost-reason"
                  className="mt-3 px-3 py-2 rounded-sm bg-rose-50 border border-rose-200 flex items-center justify-between gap-2"
                >
                  <div className="min-w-0">
                    <div className="label-tracking !text-rose-700">Lost reason</div>
                    <div className="text-sm text-rose-900 mt-0.5 truncate">{lead.lost_reason}</div>
                  </div>
                  <button
                    type="button"
                    data-testid="edit-lost-reason-btn"
                    onClick={() => setStatusDialog("lost")}
                    className="text-rose-700 hover:text-rose-900 shrink-0"
                    title="Edit lost reason"
                  >
                    <PencilSimple size={14} />
                  </button>
                </div>
              )}
            </div>

            <div>
              <Label className="label-tracking inline-flex items-center gap-1">
                <UsersIcon size={11} /> Assigned to ({assignedEmails.length})
              </Label>
              {!isGuest && (
                <AssigneeMultiPicker
                  assignableUsers={assignableUsers}
                  selected={assignedEmails}
                  onChange={setAssignees}
                />
              )}
              {assignedDetails.length > 0 ? (
                <ul className="mt-3 space-y-1.5" data-testid="assignee-list">
                  {assignedDetails.map((a) => (
                    <li
                      key={a.email}
                      data-testid={`assignee-chip-${a.email}`}
                      className="flex items-center gap-2 px-2.5 py-1.5 rounded-sm bg-slate-50 text-sm group"
                    >
                      <div className="h-6 w-6 rounded-full bg-slate-900 text-white text-[0.6rem] flex items-center justify-center shrink-0">
                        {(a.display_name || a.email).split(/[ .@]/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("")}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-slate-900 text-xs font-medium truncate">{a.display_name}</div>
                        <div className="text-[0.65rem] text-slate-500 truncate font-mono flex items-center gap-1.5">
                          <span className="truncate">{a.email}</span>
                          {a.department && (
                            <span className="text-indigo-600 normal-case font-sans">· {a.department}</span>
                          )}
                        </div>
                      </div>
                      {!isGuest && (
                        <button
                          type="button"
                          data-testid={`remove-assignee-${a.email}`}
                          onClick={() => removeAssignee(a.email)}
                          className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-600"
                          title="Remove"
                        >
                          <X size={12} />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-slate-400 mt-2">Unassigned. Use the button above to assign one or more users.</p>
              )}
              {assignableUsers.length === 0 && (
                <p className="text-xs text-slate-500 mt-2">
                  <Link to="/settings" className="underline">Sync users from Microsoft</Link> to enable assignment.
                </p>
              )}
            </div>

            <div>
              <Label className="label-tracking">Estimated Value (ZAR)</Label>
              {isGuest ? (
                <div data-testid="lead-value-readonly" className="mt-2 px-3 py-2 rounded-sm border border-slate-200 bg-slate-50 text-sm text-slate-700">
                  {lead.value != null ? `R${Number(lead.value).toLocaleString()}` : <span className="text-slate-400 italic">Not set</span>}
                </div>
              ) : (
                <Input
                  data-testid="lead-value-input"
                  type="number"
                  step="100"
                  defaultValue={lead.value || ""}
                  placeholder="e.g. 25000"
                  onBlur={(e) => {
                    const val = e.target.value ? parseFloat(e.target.value) : null;
                    if (val !== lead.value) save({ value: val });
                  }}
                  className="mt-2 rounded-sm h-10 border-slate-300 focus-visible:ring-[#002FA7] focus-visible:ring-2"
                />
              )}
            </div>
          </section>

          <section className="bg-white border border-slate-200 rounded-sm p-6 space-y-3">
            <div className="label-tracking">Internal Notes</div>
            {isGuest ? (
              <div data-testid="lead-notes-readonly" className="text-sm text-slate-700 whitespace-pre-wrap min-h-[80px]">
                {lead.notes || <span className="text-slate-400 italic">No internal notes.</span>}
              </div>
            ) : (
              <>
                <Textarea
                  data-testid="lead-notes-textarea"
                  defaultValue={lead.notes || ""}
                  placeholder="Add private notes about this lead…"
                  rows={6}
                  className="rounded-sm border-slate-300 text-sm focus-visible:ring-[#002FA7] focus-visible:ring-2"
                  id="notes-input"
                />
                <Button
                  data-testid="save-notes-btn"
                  size="sm"
                  disabled={saving}
                  onClick={() => {
                    const val = document.getElementById("notes-input").value;
                    save({ notes: val });
                  }}
                  className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-2"
                >
                  <FloppyDisk size={14} /> Save notes
                </Button>
              </>
            )}
          </section>
        </aside>
      </div>

      <StatusOutcomeDialog
        mode={statusDialog}
        lostReasons={lostReasons}
        currentLead={lead}
        onClose={() => setStatusDialog(null)}
        onConfirm={async (payload) => {
          await save(payload);
          setStatusDialog(null);
        }}
      />
    </div>
  );
}

function StatusOutcomeDialog({ mode, lostReasons, currentLead, onClose, onConfirm }) {
  const isWon = mode === "won";
  const isLost = mode === "lost";
  const open = !!mode;

  const [matterNumber, setMatterNumber] = useState("");
  const [reason, setReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMatterNumber(currentLead?.matter_number || "");
    if (currentLead?.lost_reason && lostReasons.includes(currentLead.lost_reason)) {
      setReason(currentLead.lost_reason);
      setCustomReason("");
    } else if (currentLead?.lost_reason) {
      setReason("__custom__");
      setCustomReason(currentLead.lost_reason);
    } else {
      setReason("");
      setCustomReason("");
    }
  }, [open, currentLead, lostReasons]);

  const submit = async () => {
    setSubmitting(true);
    try {
      if (isWon) {
        const mn = matterNumber.trim();
        if (!mn) { toast.error("Matter number is required"); setSubmitting(false); return; }
        await onConfirm({ status: "won", matter_number: mn });
      } else if (isLost) {
        const final = reason === "__custom__" ? customReason.trim() : reason;
        if (!final) { toast.error("Please pick a reason"); setSubmitting(false); return; }
        await onConfirm({ status: "lost", lost_reason: final });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid={`status-${mode}-dialog`} className="rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl flex items-center gap-3">
            {isWon ? (
              <><Trophy size={26} weight="fill" className="text-emerald-600" /> Mark as Won</>
            ) : (
              <><ChartLineDown size={26} weight="duotone" className="text-rose-600" /> Mark as Lost</>
            )}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            {isWon
              ? "Brilliant. Capture the matter number so the lead links to the file."
              : "A quick reason helps us learn why leads slip — it'll show up in your analytics."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {isWon && (
            <div>
              <Label className="label-tracking">Matter number</Label>
              <div className="mt-2 relative">
                <Hash size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  autoFocus
                  data-testid="matter-number-input"
                  value={matterNumber}
                  onChange={(e) => setMatterNumber(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
                  placeholder="e.g. MAT-2026-1183"
                  className="h-11 rounded-sm pl-9 font-mono text-sm focus-visible:ring-emerald-600 focus-visible:ring-2"
                />
              </div>
              <p className="text-[0.7rem] text-slate-500 mt-2 inline-flex items-start gap-1.5">
                <Info size={11} className="mt-0.5 shrink-0" />
                The matter number is also displayed under the Workflow card on the lead.
              </p>
            </div>
          )}

          {isLost && (
            <>
              <div>
                <Label className="label-tracking">Reason</Label>
                {lostReasons.length === 0 ? (
                  <p className="text-xs text-rose-600 mt-2">
                    No reasons configured. Add some under <span className="font-medium">Settings → Lost Reasons</span> first.
                  </p>
                ) : (
                  <Select value={reason} onValueChange={setReason}>
                    <SelectTrigger data-testid="lost-reason-select" className="mt-2 rounded-sm h-11">
                      <SelectValue placeholder="Choose a reason" />
                    </SelectTrigger>
                    <SelectContent>
                      {lostReasons.map((r) => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                      <SelectItem value="__custom__">Other (type your own)…</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
              {reason === "__custom__" && (
                <div>
                  <Label className="label-tracking">Custom reason</Label>
                  <Input
                    autoFocus
                    data-testid="custom-lost-reason-input"
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
                    placeholder="Describe the reason…"
                    className="mt-2 h-11 rounded-sm focus-visible:ring-rose-600 focus-visible:ring-2"
                  />
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
          <Button
            data-testid="status-confirm-btn"
            className={cn(
              "rounded-sm gap-2",
              isWon ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"
            )}
            disabled={submitting}
            onClick={submit}
          >
            {isWon ? <Trophy size={14} weight="fill" /> : <ChartLineDown size={14} weight="fill" />}
            {submitting ? "Saving…" : isWon ? "Mark as Won" : "Mark as Lost"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditableField({ label, value, onSave, icon: Icon, placeholder, mono = false, type = "text", testid, link, readOnly = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");

  useEffect(() => { setDraft(value || ""); }, [value]);

  const commit = () => {
    setEditing(false);
    if ((draft || "").trim() === (value || "").trim()) return;
    onSave(draft.trim());
  };

  // Read-only rendering — plain text, no click-to-edit affordance.
  if (readOnly) {
    return (
      <div>
        <dt className="text-xs text-slate-500 mb-1">{label}</dt>
        <div className="text-sm text-slate-900 inline-flex items-center gap-2" data-testid={testid}>
          {Icon && <Icon size={14} className="text-slate-400" />}
          {value ? (
            link ? (
              <a href={link} className={cn("hover:underline", mono && "font-mono")}>{value}</a>
            ) : (
              <span className={cn(mono && "font-mono")}>{value}</span>
            )
          ) : (
            <span className="text-slate-400 italic">—</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <dt className="text-xs text-slate-500 mb-1">{label}</dt>
      {editing ? (
        <Input
          autoFocus
          type={type}
          data-testid={`${testid}-input`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") { setDraft(value || ""); setEditing(false); }
          }}
          placeholder={placeholder}
          className={cn("h-8 rounded-sm border-slate-300 focus-visible:ring-[#002FA7] focus-visible:ring-2", mono && "font-mono text-sm")}
        />
      ) : (
        <button
          type="button"
          data-testid={testid}
          onClick={() => setEditing(true)}
          className="text-sm text-slate-900 inline-flex items-center gap-2 group hover:text-[#002FA7]"
        >
          {Icon && <Icon size={14} className="text-slate-400 group-hover:text-[#002FA7]" />}
          {value ? (
            link ? (
              <a href={link} onClick={(e) => e.stopPropagation()} className={cn("hover:underline", mono && "font-mono")}>{value}</a>
            ) : (
              <span className={cn(mono && "font-mono")}>{value}</span>
            )
          ) : (
            <span className="text-slate-400 italic">{placeholder}</span>
          )}
          <PencilSimple size={10} className="opacity-0 group-hover:opacity-60 text-slate-500" />
        </button>
      )}
    </div>
  );
}

function AssigneeMultiPicker({ assignableUsers, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(selected);
  const [search, setSearch] = useState("");

  useEffect(() => { setDraft(selected); /* eslint-disable-next-line */ }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = assignableUsers;
    if (q) {
      list = list.filter((u) =>
        (u.display_name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.job_title || "").toLowerCase().includes(q));
    }
    return list;
  }, [assignableUsers, search]);

  const toggle = (email) => {
    const e = (email || "").toLowerCase();
    setDraft((cur) => cur.includes(e) ? cur.filter((x) => x !== e) : [...cur, e]);
  };

  const commit = () => {
    setOpen(false);
    const same = draft.length === selected.length && draft.every((d) => selected.includes(d));
    if (!same) onChange(draft);
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) commit(); }}>
      <PopoverTrigger asChild>
        <Button
          data-testid="assignee-picker-btn"
          variant="outline"
          size="sm"
          className="mt-2 w-full justify-between rounded-sm h-10 border-slate-300 text-sm font-normal"
        >
          <span className="inline-flex items-center gap-2 text-slate-600">
            <Plus size={13} />
            {selected.length === 0 ? "Assign users" : `Edit assignees (${selected.length})`}
          </span>
          <CaretDown size={12} className="text-slate-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent data-testid="assignee-picker" className="w-[320px] p-0 rounded-sm" align="end">
        <div className="p-3 border-b border-slate-100">
          <div className="label-tracking">Assign one or more</div>
          <div className="relative mt-2">
            <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="assignee-search"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, title…"
              className="h-8 rounded-sm pl-7 text-xs"
            />
          </div>
        </div>
        <div className="max-h-72 overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <p className="px-3 py-3 text-xs text-slate-500">No matches.</p>
          ) : filtered.map((u) => {
            const email = (u.email || u.user_principal_name || "").toLowerCase();
            const checked = draft.includes(email);
            return (
              <label
                key={email}
                className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer text-xs"
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => toggle(email)}
                  data-testid={`assignee-check-${email}`}
                />
                <div className="min-w-0 flex-1">
                  <div className="text-slate-900 font-medium truncate">{u.display_name}</div>
                  <div className="text-[0.65rem] text-slate-500 truncate font-mono">{email}</div>
                </div>
                {u.job_title && <span className="text-[0.6rem] text-slate-400 truncate max-w-[80px]">{u.job_title}</span>}
              </label>
            );
          })}
        </div>
        <div className="flex justify-between gap-2 p-2 border-t border-slate-100">
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setDraft([]); }}>
            Clear all
          </Button>
          <Button
            size="sm"
            className="h-7 text-xs rounded-sm bg-slate-900 hover:bg-slate-800"
            onClick={commit}
            data-testid="assignee-save-btn"
          >
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}


/**
 * Body renderer for a single thread message.
 * - "html" mode: render the sanitized original HTML (best fidelity, retains
 *   formatting & signatures the way Outlook sent them).
 * - "plain" mode: render the AI-cleaned plain text, which strips most
 *   signatures and quoted noise so the actual content is easy to skim.
 * Toggle is shown only when HTML is available — otherwise we just show plain.
 */
function ThreadMessageBody({ msg, mode, onSetMode }) {
  const hasHtml = !!(msg.body_html && msg.body_html.trim());
  const effectiveMode = hasHtml ? mode : "plain";
  return (
    <div className="border-l-2 border-slate-200 pl-4">
      {hasHtml && (
        <div data-testid="thread-body-mode-toggle" className="mb-2 inline-flex rounded-sm border border-slate-200 bg-slate-50 p-0.5 text-[10px] font-medium uppercase tracking-wider">
          <button
            type="button"
            onClick={() => onSetMode("html")}
            className={cn(
              "px-2 py-1 rounded-sm transition-colors",
              effectiveMode === "html" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
            )}
          >
            HTML
          </button>
          <button
            type="button"
            onClick={() => onSetMode("plain")}
            className={cn(
              "px-2 py-1 rounded-sm transition-colors",
              effectiveMode === "plain" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
            )}
          >
            Plain
          </button>
        </div>
      )}
      {effectiveMode === "html" ? (
        <div
          data-testid="thread-body-html"
          className="email-thread-body prose prose-sm max-w-none text-slate-800 [&_blockquote]:border-l-2 [&_blockquote]:border-slate-200 [&_blockquote]:pl-3 [&_blockquote]:text-slate-500 [&_img]:max-w-full [&_a]:break-words [&_table]:border [&_table]:border-slate-200 [&_table_td]:p-1 [&_table_td]:border [&_table_td]:border-slate-100"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(msg.body_html, {
              USE_PROFILES: { html: true },
              FORBID_TAGS: ["script", "style"],
              FORBID_ATTR: ["onerror", "onclick", "onload"],
            }),
          }}
        />
      ) : (
        <pre data-testid="thread-body-plain" className="text-xs text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">
          {msg.body || "(no plain text)"}
        </pre>
      )}
    </div>
  );
}
